import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

interface UseKeyboardShortcutsOptions {
  onShowShortcuts: () => void
}

/**
 * Global keyboard shortcuts hook.
 * Handles:
 * - ? to show shortcuts panel
 * - Esc to close shortcuts panel (via onShowShortcuts)
 * - / to focus chat input
 * - g+d, g+t, g+c, g+h navigation sequences
 */
export default function useKeyboardShortcuts({
  onShowShortcuts,
}: UseKeyboardShortcutsOptions) {
  const navigate = useNavigate()
  const location = useLocation()
  const [pendingChord, setPendingChord] = useState<string | null>(null)
  const chordTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea
      const target = e.target as HTMLElement
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable

      // Handle Escape - always works
      if (e.key === 'Escape') {
        // Close shortcuts panel by calling the callback
        onShowShortcuts()
        return
      }

      // Only process other shortcuts when not typing
      if (isTyping) return

      // Handle ? to show shortcuts
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        onShowShortcuts()
        return
      }

      // Handle / to focus chat input (only on chat page)
      if (e.key === '/' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        if (location.pathname === '/chat') {
          e.preventDefault()
          const textarea = document.querySelector('textarea')
          textarea?.focus()
        }
        return
      }

      // Handle chord sequences starting with 'g'
      if (e.key === 'g' && !pendingChord && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        setPendingChord('g')

        // Clear chord after 1.5 seconds
        if (chordTimeoutRef.current) {
          clearTimeout(chordTimeoutRef.current)
        }
        chordTimeoutRef.current = window.setTimeout(() => {
          setPendingChord(null)
        }, 1500)
        return
      }

      // Complete chord sequences
      if (pendingChord === 'g') {
        e.preventDefault()

        if (chordTimeoutRef.current) {
          clearTimeout(chordTimeoutRef.current)
        }
        setPendingChord(null)

        switch (e.key) {
          case 'd':
            navigate('/dashboard')
            break
          case 't':
            navigate('/todos')
            break
          case 'c':
            navigate('/canon')
            break
          case 'h':
            navigate('/dashboard')
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      if (chordTimeoutRef.current) {
        clearTimeout(chordTimeoutRef.current)
      }
    }
  }, [navigate, location, onShowShortcuts, pendingChord])

  return { pendingChord }
}
